import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import {
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  TextField,
} from "@mui/material";

import Constants from "Helpers/Constants";
import init from "Helpers/WindowToken";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

const emptyForm = {
  name: "",
  subscription_discount_percentage: "",
  other_purchase_discount_percentage: "",
  start_at: "",
  end_at: "",
  reason: "",
};

const sectionCardSx = {
  overflow: "hidden",
  background: "linear-gradient(145deg, #202A43 0%, #182238 100%)",
  border: "1px solid rgba(148, 163, 184, 0.18)",
  boxShadow: "0 18px 42px rgba(2, 8, 23, 0.2)",
};

const dialogPaperSx = {
  color: "#ffffff",
  borderRadius: "18px",
  background: "linear-gradient(145deg, #202940 0%, #171E2F 100%)",
  border: "1px solid rgba(255, 255, 255, 0.1)",
  boxShadow: "0 24px 80px rgba(0, 0, 0, 0.45)",
};

const offerFieldSx = {
  "& .MuiOutlinedInput-root": {
    color: "#ffffff",
    backgroundColor: "rgba(255, 255, 255, 0.055)",
    "& fieldset": { borderColor: "rgba(255, 255, 255, 0.2)" },
    "&:hover fieldset": { borderColor: "rgba(255, 255, 255, 0.38)" },
    "&.Mui-focused fieldset": { borderColor: "#66BB6A" },
  },
  "& .MuiInputLabel-root": { color: "rgba(255, 255, 255, 0.72)" },
  "& .MuiInputLabel-root.Mui-focused": { color: "#81C784" },
  "& .MuiInputBase-input": { color: "#ffffff" },
  "& input[type='datetime-local']::-webkit-calendar-picker-indicator": {
    filter: "invert(1)",
    opacity: 0.78,
    cursor: "pointer",
  },
};

const statusTheme = {
  ACTIVE: ["Active", "#86EFAC", "rgba(34, 197, 94, 0.16)", "rgba(74, 222, 128, 0.32)"],
  SCHEDULED: ["Scheduled", "#7DD3FC", "rgba(14, 165, 233, 0.14)", "rgba(56, 189, 248, 0.3)"],
  INACTIVE: ["Inactive", "#CBD5E1", "rgba(148, 163, 184, 0.14)", "rgba(148, 163, 184, 0.28)"],
  CANCELLED: ["Inactive", "#CBD5E1", "rgba(148, 163, 184, 0.14)", "rgba(148, 163, 184, 0.28)"],
  EXPIRED: ["Expired", "#FCA5A5", "rgba(239, 68, 68, 0.14)", "rgba(248, 113, 113, 0.28)"],
};

const toLocalInput = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};

const formatDate = (value) => {
  const parsed = new Date(value);
  if (!value || Number.isNaN(parsed.getTime())) return { date: "-", time: "" };
  return {
    date: parsed.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }),
    time: parsed.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true }),
  };
};

const isExpired = (offer) => {
  const endAt = new Date(offer?.end_at);
  return !Number.isNaN(endAt.getTime()) && endAt <= new Date();
};

const getStatusDescription = (status) => {
  if (status === "ACTIVE") return "Currently applied to eligible purchases";
  if (status === "SCHEDULED") return "Will start automatically at the configured time";
  if (status === "EXPIRED") return "The configured offer period has ended";
  return "Not used for prices or payment calculations";
};

function PurchaseOfferManager({ userId, onNotify }) {
  const isOverall = !userId;
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [updatingStatusId, setUpdatingStatusId] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [activateOnSave, setActivateOnSave] = useState(false);
  const [pendingDeactivation, setPendingDeactivation] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const listUrl = isOverall
    ? Constants.getUrls.getOverallPurchaseOffers
    : `${Constants.getUrls.getUserPurchaseOffers}${userId}/purchase-offers`;

  const loadOffers = useCallback(async () => {
    if (!isOverall && !userId) return;
    try {
      setLoading(true);
      const response = await axios.get(listUrl);
      const data = isOverall ? response?.data?.data || [] : response?.data?.data?.offers || [];
      setOffers(Array.isArray(data) ? data : []);
    } catch (error) {
      onNotify(error?.response?.data?.message || "Unable to load offers.", "error");
    } finally {
      setLoading(false);
    }
  }, [isOverall, listUrl, onNotify, userId]);

  useEffect(() => {
    loadOffers();
  }, [loadOffers]);

  const summary = useMemo(
    () => ({
      total: offers.length,
      active: offers.filter((offer) => offer?.computed_status === "ACTIVE").length,
      scheduled: offers.filter((offer) => offer?.computed_status === "SCHEDULED").length,
    }),
    [offers]
  );

  const openCreate = () => {
    setEditingOffer(null);
    setActivateOnSave(false);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (offer, shouldActivate = false) => {
    setEditingOffer(offer);
    setActivateOnSave(shouldActivate);
    setForm({
      name: offer?.name || "",
      subscription_discount_percentage: offer?.subscription_discount_percentage ?? "",
      other_purchase_discount_percentage: offer?.other_purchase_discount_percentage ?? "",
      start_at: toLocalInput(offer?.start_at),
      end_at: toLocalInput(offer?.end_at),
      reason: offer?.reason || "",
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogOpen(false);
    setEditingOffer(null);
    setActivateOnSave(false);
    setForm(emptyForm);
  };

  const getUpdateUrl = (offerId) =>
    isOverall
      ? `${Constants.putUrls.updateOverallPurchaseOffer}${offerId}`
      : `${Constants.putUrls.updateUserPurchaseOffer}${userId}/purchase-offers/${offerId}`;

  const buildPayload = () => {
    const payload = {
      name: form.name.trim(),
      subscription_discount_percentage: Number(form.subscription_discount_percentage || 0),
      other_purchase_discount_percentage: Number(form.other_purchase_discount_percentage || 0),
      start_at: form.start_at ? new Date(form.start_at).toISOString() : "",
      end_at: form.end_at ? new Date(form.end_at).toISOString() : "",
      reason: form.reason.trim(),
    };

    if (!editingOffer || activateOnSave) payload.is_active = true;
    return payload;
  };

  const getValidationError = () => {
    if (!form.name.trim()) return "Offer name is required.";
    if (!form.reason.trim()) return "Reason / internal note is required.";

    const subscription = Number(form.subscription_discount_percentage || 0);
    const other = Number(form.other_purchase_discount_percentage || 0);
    if (
      !Number.isFinite(subscription) ||
      !Number.isFinite(other) ||
      subscription < 0 ||
      subscription > 99 ||
      other < 0 ||
      other > 99
    ) {
      return "Offer percentages must be between 0 and 99.";
    }
    if (subscription === 0 && other === 0) {
      return "Enter a subscription or other-purchase offer percentage.";
    }

    const startAt = new Date(form.start_at);
    const endAt = new Date(form.end_at);
    if (Number.isNaN(startAt.getTime()) || Number.isNaN(endAt.getTime())) {
      return "Offer start and end time are required.";
    }
    if (endAt <= startAt) return "Offer end time must be after its start time.";
    if (activateOnSave && endAt <= new Date()) {
      return "Set a future end time before activating this offer.";
    }
    return "";
  };

  const saveOffer = async () => {
    if (init() !== "success") {
      window.location.assign("/signin");
      return;
    }

    const validationError = getValidationError();
    if (validationError) {
      onNotify(validationError, "error");
      return;
    }

    try {
      setSaving(true);
      const payload = buildPayload();
      if (editingOffer?._id) {
        await axios.patch(getUpdateUrl(editingOffer._id), payload);
      } else {
        const createUrl = isOverall
          ? Constants.postUrls.createOverallPurchaseOffer
          : `${Constants.postUrls.createUserPurchaseOffer}${userId}/purchase-offers`;
        await axios.post(createUrl, payload);
      }
      onNotify(
        activateOnSave
          ? "Offer updated and activated successfully."
          : `${isOverall ? "Overall" : "User"} offer saved successfully.`,
        "success"
      );
      closeDialog();
      await loadOffers();
    } catch (error) {
      onNotify(error?.response?.data?.message || "Unable to save offer.", "error");
    } finally {
      setSaving(false);
    }
  };

  const updateOfferStatus = async (offer, nextIsActive) => {
    if (!offer?._id || updatingStatusId) return;
    try {
      setUpdatingStatusId(offer._id);
      const response = await axios.patch(getUpdateUrl(offer._id), { is_active: nextIsActive });
      onNotify(
        response?.data?.message ||
          `Offer ${nextIsActive ? "activated" : "deactivated"} successfully.`,
        "success"
      );
      setPendingDeactivation(null);
      await loadOffers();
    } catch (error) {
      onNotify(
        error?.response?.data?.message ||
          `Unable to ${nextIsActive ? "activate" : "deactivate"} offer.`,
        "error"
      );
    } finally {
      setUpdatingStatusId("");
    }
  };

  const activateOffer = (offer) => {
    if (isExpired(offer)) {
      openEdit(offer, true);
      onNotify("Update the expired offer period, then save to activate it.", "info");
      return;
    }
    updateOfferStatus(offer, true);
  };

  const renderOffer = (offer) => {
    const computedStatus = offer?.computed_status || "EXPIRED";
    const theme = statusTheme[computedStatus] || statusTheme.EXPIRED;
    const startsAt = formatDate(offer?.start_at);
    const endsAt = formatDate(offer?.end_at);
    const statusUpdating = updatingStatusId === offer?._id;

    return (
      <Grid item xs={12} lg={6} key={offer?._id}>
        <MDBox
          height="100%"
          p={{ xs: 2, md: 2.5 }}
          borderRadius="lg"
          sx={{
            background: "linear-gradient(145deg, rgba(15,23,42,.58), rgba(30,41,59,.52))",
            border: `1px solid ${theme[3]}`,
            boxShadow: "0 12px 30px rgba(2, 8, 23, 0.14)",
          }}
        >
          <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
            <MDBox minWidth={0}>
              <MDTypography
                variant="h6"
                sx={{ color: "#F8FAFC", fontWeight: 700, wordBreak: "break-word" }}
              >
                {offer?.name || "Unnamed offer"}
              </MDTypography>
              <MDTypography
                variant="caption"
                display="block"
                mt={0.3}
                sx={{ color: "rgba(203,213,225,.72)" }}
              >
                {getStatusDescription(computedStatus)}
              </MDTypography>
            </MDBox>
            <Chip
              size="small"
              label={theme[0]}
              sx={{
                flexShrink: 0,
                color: theme[1],
                bgcolor: theme[2],
                border: `1px solid ${theme[3]}`,
                fontWeight: 800,
                textTransform: "uppercase",
                letterSpacing: ".04em",
              }}
            />
          </MDBox>

          <Grid container spacing={1.25} mt={1.25}>
            <Grid item xs={6}>
              <MDBox
                p={1.5}
                borderRadius="md"
                sx={{ bgcolor: "rgba(59,130,246,.1)", border: "1px solid rgba(96,165,250,.18)" }}
              >
                <MDTypography variant="caption" sx={{ color: "#93C5FD" }}>
                  SUBSCRIPTION
                </MDTypography>
                <MDTypography variant="h5" sx={{ color: "#EFF6FF", fontWeight: 800 }}>
                  {Number(offer?.subscription_discount_percentage || 0)}%
                </MDTypography>
              </MDBox>
            </Grid>
            <Grid item xs={6}>
              <MDBox
                p={1.5}
                borderRadius="md"
                sx={{ bgcolor: "rgba(168,85,247,.1)", border: "1px solid rgba(192,132,252,.18)" }}
              >
                <MDTypography variant="caption" sx={{ color: "#D8B4FE" }}>
                  OTHER PURCHASES
                </MDTypography>
                <MDTypography variant="h5" sx={{ color: "#FAF5FF", fontWeight: 800 }}>
                  {Number(offer?.other_purchase_discount_percentage || 0)}%
                </MDTypography>
              </MDBox>
            </Grid>
          </Grid>

          <MDBox
            mt={1.5}
            p={1.5}
            borderRadius="md"
            sx={{ bgcolor: "rgba(255,255,255,.035)", border: "1px solid rgba(148,163,184,.12)" }}
          >
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <MDTypography variant="caption" sx={{ color: "#94A3B8" }}>STARTS</MDTypography>
                <MDTypography variant="button" display="block" sx={{ color: "#E2E8F0" }}>
                  {startsAt.date}
                </MDTypography>
                <MDTypography variant="caption" sx={{ color: "#AAB5C5" }}>{startsAt.time}</MDTypography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <MDTypography variant="caption" sx={{ color: "#94A3B8" }}>ENDS</MDTypography>
                <MDTypography variant="button" display="block" sx={{ color: "#E2E8F0" }}>
                  {endsAt.date}
                </MDTypography>
                <MDTypography variant="caption" sx={{ color: "#AAB5C5" }}>{endsAt.time}</MDTypography>
              </Grid>
            </Grid>
          </MDBox>

          {offer?.reason ? (
            <MDBox mt={1.5}>
              <MDTypography variant="caption" sx={{ color: "#94A3B8" }}>INTERNAL NOTE</MDTypography>
              <MDTypography
                variant="caption"
                display="block"
                mt={0.25}
                sx={{ color: "rgba(226,232,240,.78)", lineHeight: 1.5 }}
              >
                {offer.reason}
              </MDTypography>
            </MDBox>
          ) : null}

          <Divider sx={{ my: 1.75, borderColor: "rgba(148,163,184,.14)" }} />
          <MDBox display="flex" justifyContent="space-between" gap={1} flexWrap="wrap">
            <MDButton
              size="small"
              variant="outlined"
              color="info"
              disabled={statusUpdating}
              onClick={() => openEdit(offer)}
            >
              Edit details
            </MDButton>
            {offer?.is_active && computedStatus === "EXPIRED" ? (
              <MDBox display="flex" gap={1} flexWrap="wrap">
                <MDButton
                  size="small"
                  color="success"
                  disabled={statusUpdating}
                  onClick={() => openEdit(offer)}
                >
                  Renew offer
                </MDButton>
                <MDButton
                  size="small"
                  color="warning"
                  disabled={statusUpdating}
                  onClick={() => setPendingDeactivation(offer)}
                >
                  Deactivate
                </MDButton>
              </MDBox>
            ) : offer?.is_active ? (
              <MDButton
                size="small"
                color="warning"
                disabled={statusUpdating}
                onClick={() => setPendingDeactivation(offer)}
              >
                {statusUpdating ? "Updating..." : "Deactivate"}
              </MDButton>
            ) : (
              <MDButton
                size="small"
                color="success"
                disabled={statusUpdating}
                onClick={() => activateOffer(offer)}
              >
                {statusUpdating ? "Updating..." : isExpired(offer) ? "Renew & activate" : "Activate"}
              </MDButton>
            )}
          </MDBox>
        </MDBox>
      </Grid>
    );
  };

  const summaryLabel =
    summary.total +
    " total • " +
    summary.active +
    " active" +
    (summary.scheduled ? " • " + summary.scheduled + " scheduled" : "");

  return (
    <>
      <Card sx={{ ...sectionCardSx, ...(userId ? { mt: 3, mb: 3 } : {}) }}>
        <MDBox
          px={{ xs: 2, md: 3 }}
          py={2.5}
          sx={{
            background: "linear-gradient(90deg, rgba(16,185,129,.1), transparent 48%)",
            borderBottom: "1px solid rgba(148,163,184,.14)",
          }}
        >
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
            flexDirection={{ xs: "column", md: "row" }}
            gap={2}
          >
            <MDBox>
              <MDBox display="flex" alignItems="center" flexWrap="wrap" gap={1.25}>
                <MDTypography variant="h5" sx={{ color: "#F8FAFC", fontWeight: 700 }}>
                  {isOverall ? "Overall Offers" : "User-specific Offers"}
                </MDTypography>
                {!loading ? (
                  <Chip
                    size="small"
                    label={summaryLabel}
                    sx={{
                      color: "#D1FAE5",
                      bgcolor: "rgba(16,185,129,.13)",
                      border: "1px solid rgba(52,211,153,.22)",
                      fontWeight: 700,
                    }}
                  />
                ) : null}
              </MDBox>
              <MDTypography
                variant="button"
                display="block"
                mt={0.7}
                sx={{ color: "rgba(226,232,240,.76)", maxWidth: 850, lineHeight: 1.55 }}
              >
                Subscription and other-purchase discounts are calculated separately. When user and
                overall offers both apply, the higher final price is used—discounts never stack.
              </MDTypography>
            </MDBox>
            <MDButton color="success" size="small" onClick={openCreate} sx={{ whiteSpace: "nowrap" }}>
              + Create Offer
            </MDButton>
          </MDBox>
        </MDBox>

        <MDBox px={{ xs: 2, md: 3 }} py={3}>
          {loading ? (
            <MDBox display="flex" alignItems="center" justifyContent="center" gap={1.5} py={5}>
              <CircularProgress size={22} sx={{ color: "#34D399" }} />
              <MDTypography variant="button" sx={{ color: "rgba(226,232,240,.72)" }}>
                Loading offers...
              </MDTypography>
            </MDBox>
          ) : offers.length ? (
            <Grid container spacing={2}>
              {offers.map(renderOffer)}
            </Grid>
          ) : (
            <MDBox
              py={5}
              px={2}
              textAlign="center"
              borderRadius="lg"
              sx={{ bgcolor: "rgba(15,23,42,.34)", border: "1px dashed rgba(148,163,184,.3)" }}
            >
              <MDTypography variant="h6" sx={{ color: "#E2E8F0" }}>
                No offers created yet
              </MDTypography>
              <MDTypography variant="button" display="block" mt={0.5} sx={{ color: "#94A3B8" }}>
                Create a time-limited discount for subscriptions, other purchases, or both.
              </MDTypography>
              <MDButton color="success" size="small" sx={{ mt: 2 }} onClick={openCreate}>
                Create first offer
              </MDButton>
            </MDBox>
          )}
        </MDBox>
      </Card>

      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle sx={{ color: "#fff", pb: 0.75 }}>
          {activateOnSave
            ? "Renew and Activate Offer"
            : editingOffer
            ? "Edit Offer"
            : "Create " + (isOverall ? "Overall" : "User") + " Offer"}
        </DialogTitle>
        <DialogContent sx={{ pt: "12px !important" }}>
          <MDBox
            mb={2.5}
            p={1.5}
            borderRadius="md"
            sx={{
              bgcolor: activateOnSave ? "rgba(245,158,11,.1)" : "rgba(16,185,129,.08)",
              border: activateOnSave
                ? "1px solid rgba(251,191,36,.22)"
                : "1px solid rgba(52,211,153,.18)",
            }}
          >
            <MDTypography
              variant="button"
              display="block"
              sx={{
                color: activateOnSave ? "#FDE68A" : "rgba(226,232,240,.82)",
                lineHeight: 1.55,
              }}
            >
              {activateOnSave
                ? "This offer has expired. Set a future end time; saving will reactivate it."
                : "Set either percentage or both. The offer applies only during this server-controlled time window."}
            </MDTypography>
          </MDBox>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                required
                sx={offerFieldSx}
                fullWidth
                label="Offer name"
                value={form.name}
                onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                sx={offerFieldSx}
                fullWidth
                type="number"
                inputProps={{ min: 0, max: 99, step: 0.01 }}
                label="Subscription offer %"
                value={form.subscription_discount_percentage}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    subscription_discount_percentage: event.target.value,
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                sx={offerFieldSx}
                fullWidth
                type="number"
                inputProps={{ min: 0, max: 99, step: 0.01 }}
                label="Other purchases offer %"
                value={form.other_purchase_discount_percentage}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    other_purchase_discount_percentage: event.target.value,
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                sx={offerFieldSx}
                fullWidth
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                label="Starts at"
                value={form.start_at}
                onChange={(event) => setForm((current) => ({ ...current, start_at: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                sx={offerFieldSx}
                fullWidth
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                label="Ends at"
                value={form.end_at}
                onChange={(event) => setForm((current) => ({ ...current, end_at: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                sx={offerFieldSx}
                fullWidth
                multiline
                minRows={2}
                label="Reason / internal note"
                value={form.reason}
                onChange={(event) => setForm((current) => ({ ...current, reason: event.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5 }}>
          <MDButton color="secondary" disabled={saving} onClick={closeDialog}>
            Cancel
          </MDButton>
          <MDButton color="success" disabled={saving} onClick={saveOffer}>
            {saving ? "Saving..." : activateOnSave ? "Save & Activate" : "Save Offer"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(pendingDeactivation)}
        onClose={updatingStatusId ? undefined : () => setPendingDeactivation(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle sx={{ color: "#F8FAFC" }}>Deactivate this offer?</DialogTitle>
        <DialogContent>
          <MDTypography
            variant="button"
            display="block"
            sx={{ color: "rgba(226,232,240,.78)", lineHeight: 1.6 }}
          >
            <strong style={{ color: "#fff" }}>{pendingDeactivation?.name}</strong> will stop
            affecting displayed prices and payment calculations immediately. Its details will be
            kept so you can activate it later.
          </MDTypography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <MDButton
            color="secondary"
            disabled={Boolean(updatingStatusId)}
            onClick={() => setPendingDeactivation(null)}
          >
            Keep Active
          </MDButton>
          <MDButton
            color="warning"
            disabled={Boolean(updatingStatusId)}
            onClick={() => updateOfferStatus(pendingDeactivation, false)}
          >
            {updatingStatusId ? "Deactivating..." : "Deactivate Offer"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

PurchaseOfferManager.propTypes = {
  userId: PropTypes.string,
  onNotify: PropTypes.func,
};

PurchaseOfferManager.defaultProps = {
  userId: "",
  onNotify: () => {},
};

export default PurchaseOfferManager;
